import React from "react";
import PropTypes from "prop-types";
import {
    Card,
    CardBody,
    CardTitle,
    Input,
    Button,
    FormGroup,
    Label,
    Row,
    Col,
    Spinner,
} from "reactstrap";

/**
 * Plan Info Update Forms Component
 * Contains forms for updating plan information
 */
const PlanInfoForms = ({
    additionalOrdersPrice,
    newTrialDays,
    isExceedTrialDays,
    isFreePlan,
    is_active,
    onInputChange,
    onUpdatePlanInfo,
    onToggleFreePlan,
    onToggleVeeperDisable,
    onCancelPlan,
    store_details,
    loading = false,
}) => {
    const planInfoFields = [
        { key: 'plan', label: 'Plan Name', icon: 'fa fa-tag', color: '#556ee6', value: store_details.plan },
        {
            key: 'plan_status',
            label: 'Status',
            icon: 'fa fa-check-circle',
            color: store_details.plan_status === 'ACTIVE' ? '#34c38f' : '#f46a6a',
            value: store_details.plan_status,
            badge: true
        },
        { key: 'price', label: 'Price', icon: 'fa fa-dollar-sign', color: '#f1b44c', value: store_details?.plan_info?.price },
        { key: 'interval', label: 'Interval', icon: 'fa fa-calendar', color: '#50a5f1', value: store_details?.plan_info?.interval },
        { key: 'orders', label: 'Orders', icon: 'fa fa-shopping-cart', color: '#556ee6', value: store_details?.plan_info?.orders },
        { key: 'additionalOrders', label: 'Additional Orders', icon: 'fa fa-plus-circle', color: '#34c38f', value: store_details?.plan_info?.additionalOrders },
        { key: 'additionalOrdersPrice', label: 'Additional Orders Price', icon: 'fa fa-money-bill', color: '#f1b44c', value: store_details?.plan_info?.additionalOrdersPrice },
    ];

    const infoItemStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: '1px solid #e9ecef',
    };

    const iconStyle = {
        width: '32px',
        height: '32px',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '12px',
        fontSize: '14px',
        flexShrink: 0
    };

    return (
        <Row>
            {/* Left Column */}
            <Col lg={6} className="mb-4">
                {/* Current Plan Info Card */}
                <Card className="border-0 shadow-sm mb-4">
                    <CardBody style={{ padding: '20px' }}>
                        <CardTitle className="mb-4" style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
                            <i className="fa fa-info-circle me-2" style={{ color: '#556ee6' }}></i>
                            Current Plan Information
                        </CardTitle>
                        <div>
                            {planInfoFields.filter(field => field.value).map((field, index) => (
                                <div
                                    key={field.key}
                                    style={{
                                        ...infoItemStyle,
                                        borderBottom: index === planInfoFields.filter(f => f.value).length - 1 ? 'none' : infoItemStyle.borderBottom,
                                        paddingLeft: '4px',
                                        paddingRight: '4px'
                                    }}
                                >
                                    <div style={{ ...iconStyle, backgroundColor: `${field.color}15`, color: field.color }}>
                                        <i className={field.icon}></i>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '12px', color: '#74788d', marginBottom: '2px' }}>
                                            {field.label}
                                        </div>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: field.badge ? field.color : '#495057'
                                        }}>
                                            {field.value}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardBody>
                </Card>

                {/* Free Plan Toggle */}
                <Card className="border-0 shadow-sm mb-4">
                    <CardBody style={{ padding: '20px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div style={{ flex: 1 }}>
                                <CardTitle className="mb-1" style={{ fontSize: '16px', fontWeight: '600', color: '#495057' }}>
                                    <i className="fa fa-gift me-2" style={{ color: '#f1b44c' }}></i>
                                    Free Plan
                                </CardTitle>
                                <p style={{ fontSize: '13px', color: '#74788d', marginBottom: 0 }}>
                                    Switch to free plan for this store
                                </p>
                            </div>
                            <FormGroup className="mb-0">
                                <div className="form-check form-switch">
                                    <Input
                                        type="checkbox"
                                        name="is_free_plan"
                                        className="form-check-input"
                                        id="formSwitchChecked"
                                        onChange={onToggleFreePlan}
                                        checked={isFreePlan}
                                        disabled={loading}
                                        style={{
                                            width: '48px',
                                            height: '24px',
                                            cursor: loading ? 'not-allowed' : 'pointer'
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </div>
                    </CardBody>
                </Card>

                {/* Disable Veeper Toggle */}
                <Card className="border-0 shadow-sm">
                    <CardBody style={{ padding: '20px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div style={{ flex: 1 }}>
                                <CardTitle className="mb-1" style={{ fontSize: '16px', fontWeight: '600', color: '#495057' }}>
                                    <i
                                        className={`fa me-2 ${is_active ? 'fa-toggle-on' : 'fa-toggle-off'}`}
                                        style={{ color: is_active ? '#34c38f' : '#f46a6a' }}
                                    ></i>
                                    Disable Veeper
                                </CardTitle>
                                <p style={{ fontSize: '13px', color: '#74788d', marginBottom: 0 }}>
                                    {is_active ? 'Veeper is currently enabled' : 'Veeper is currently disabled'}
                                </p>
                            </div>
                            <FormGroup className="mb-0">
                                <div className="form-check form-switch">
                                    <Input
                                        type="checkbox"
                                        name="is_disabled"
                                        className="form-check-input"
                                        id="formVeeperSwitchChecked"
                                        onChange={onToggleVeeperDisable}
                                        checked={is_active}
                                        disabled={loading}
                                        style={{
                                            width: '48px',
                                            height: '24px',
                                            cursor: loading ? 'not-allowed' : 'pointer'
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </div>
                    </CardBody>
                </Card>
            </Col>

            {/* Right Column */}
            <Col lg={6} className="mb-4">
                {/* Update Plan Info Forms */}
                <Card className="border-0 shadow-sm mb-4">
                    <CardBody style={{ padding: '20px' }}>
                        <CardTitle className="mb-4" style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
                            <i className="fa fa-edit me-2" style={{ color: '#556ee6' }}></i>
                            Update Plan Information
                        </CardTitle>

                        <FormGroup className="mb-4">
                            <Label style={{ fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '8px' }}>
                                <i className="fa fa-money-bill me-2" style={{ color: '#f1b44c' }}></i>
                                Additional Orders Price
                            </Label>
                            <div className="d-flex" style={{ gap: '8px' }}>
                                <Input
                                    name="additionalOrdersPrice"
                                    type="number"
                                    value={additionalOrdersPrice}
                                    onChange={onInputChange}
                                    placeholder="Enter additional orders price"
                                    style={{ borderRadius: '6px', flex: 1 }}
                                />
                                <Button
                                    className="btn btn-primary"
                                    onClick={() => onUpdatePlanInfo("additionalOrdersPrice")}
                                    style={{ borderRadius: '6px', minWidth: '100px' }}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-save me-2"></i>Update
                                        </>
                                    )}
                                </Button>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label style={{ fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '8px' }}>
                                <i className="fa fa-calendar-plus me-2" style={{ color: '#50a5f1' }}></i>
                                Add Trial Days
                            </Label>
                            <div className="d-flex" style={{ gap: '8px' }}>
                                <Input
                                    name="newTrialDays"
                                    type="number"
                                    value={newTrialDays}
                                    onChange={onInputChange}
                                    placeholder="Enter trial days"
                                    style={{ borderRadius: '6px', flex: 1 }}
                                    disabled={isExceedTrialDays}
                                />
                                <Button
                                    className="btn btn-primary"
                                    onClick={() => onUpdatePlanInfo("trialDays")}
                                    disabled={isExceedTrialDays || loading}
                                    style={{ borderRadius: '6px', minWidth: '100px' }}
                                >
                                    {loading ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-save me-2"></i>Update
                                        </>
                                    )}
                                </Button>
                            </div>
                            {isExceedTrialDays && (
                                <small className="text-danger d-block mt-2">
                                    <i className="fa fa-exclamation-triangle me-1"></i>
                                    Cannot exceed trial days limit
                                </small>
                            )}
                        </FormGroup>
                    </CardBody>
                </Card>

                {/* Cancel Plan Card */}
                {store_details.plan_status === 'ACTIVE' && (
                    <Card className="border-0 shadow-sm mb-4" style={{ borderLeft: '4px solid #f46a6a' }}>
                        <CardBody style={{ padding: '20px' }}>
                            <CardTitle className="mb-3" style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
                                <i className="fa fa-times-circle me-2" style={{ color: '#f46a6a' }}></i>
                                Cancel Plan
                            </CardTitle>
                            <p style={{ fontSize: '14px', color: '#74788d', marginBottom: '20px' }}>
                                This action will cancel the current active plan. This cannot be undone.
                            </p>
                            <Button
                                className="btn btn-danger"
                                onClick={onCancelPlan}
                                style={{ borderRadius: '6px' }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Canceling...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-ban me-2"></i>Cancel Plan
                                    </>
                                )}
                            </Button>
                        </CardBody>
                    </Card>
                )}

                {/* Subscription Info */}
                <Card className="border-0 shadow-sm">
                    <CardBody style={{ padding: '20px' }}>
                        <CardTitle className="mb-4" style={{ fontSize: '18px', fontWeight: '600', color: '#495057' }}>
                            <i className="fa fa-file-contract me-2" style={{ color: '#556ee6' }}></i>
                            Subscription Information
                        </CardTitle>
                        <div>
                            {(() => {
                                const subscriptionFields = [
                                    { key: 'name', label: 'Name', icon: 'fa fa-tag', color: '#556ee6' },
                                    { key: 'status', label: 'Status', icon: 'fa fa-info-circle', color: '#34c38f' },
                                    { key: 'trialDays', label: 'Trial Days', icon: 'fa fa-calendar-check', color: '#f1b44c' },
                                    { key: 'createdAt', label: 'Created At', icon: 'fa fa-clock', color: '#50a5f1' },
                                    { key: 'currentPeriodEnd', label: 'Current Period End', icon: 'fa fa-calendar-times', color: '#f46a6a' },
                                ];

                                const visibleFields = subscriptionFields.filter(field =>
                                    store_details?.subscription_info?.[field.key]
                                );

                                if (visibleFields.length === 0) {
                                    return (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '40px 20px',
                                            color: '#adb5bd',
                                            fontSize: '14px'
                                        }}>
                                            <i className="fa fa-info-circle me-2"></i>
                                            No subscription information available
                                        </div>
                                    );
                                }

                                return visibleFields.map((field, index) => {
                                    const value = store_details?.subscription_info?.[field.key];
                                    return (
                                        <div
                                            key={field.key}
                                            style={{
                                                ...infoItemStyle,
                                                borderBottom: index === visibleFields.length - 1 ? 'none' : infoItemStyle.borderBottom,
                                                paddingLeft: '4px',
                                                paddingRight: '4px'
                                            }}
                                        >
                                            <div style={{ ...iconStyle, backgroundColor: `${field.color}15`, color: field.color }}>
                                                <i className={field.icon}></i>
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: '12px', color: '#74788d', marginBottom: '2px' }}>
                                                    {field.label}
                                                </div>
                                                <div style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                                    {value}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

PlanInfoForms.propTypes = {
    additionalOrdersPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    newTrialDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isExceedTrialDays: PropTypes.bool,
    isFreePlan: PropTypes.bool.isRequired,
    is_active: PropTypes.bool.isRequired,
    onInputChange: PropTypes.func.isRequired,
    onUpdatePlanInfo: PropTypes.func.isRequired,
    onToggleFreePlan: PropTypes.func.isRequired,
    onToggleVeeperDisable: PropTypes.func.isRequired,
    onCancelPlan: PropTypes.func.isRequired,
    store_details: PropTypes.object.isRequired,
    loading: PropTypes.bool,
};

export default PlanInfoForms;

